import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { subscribeToUpdates } from 'src/sockets/ContentGenSocket';
import {
  getInitialProductData,
  getColumnDefsV2,
  getColumnDefsReview,
  adaptInitialData,
  getColumnDefsGenerate,
  getColumnDefsEdit
} from '../../utils';
import { ContentGenerationRecord, GenerationStatus } from '../../types';
import { useContentGenerationStore } from './store';
import type { ContentGenerationStore } from './store';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { ENTITY_TABLE_PAGE_SIZE } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import { useBeaconFilters } from 'src/utils/Hooks/useDorisApplyEntityFilters';
import { useBeaconRoutes } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { BEACON_SUBTABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';

interface ContentGenerationUpdate {
  beaconClientId: string;
  status: string;
  skuContentGenerationId: string;
  retailerSku: string;
  profileName: string;
  profileId: string;
  profileVersion?: number;
  version?: number;
  updatedAt: string;
  [key: string]: any;
}

export const CONTENT_GENERATION_QUERY_KEY = 'contentGenerationData';

/**
 * Helper type for the infinite query data structure
 */
type InfiniteContentGenerationData = {
  pages: { records: ContentGenerationRecord[]; totalCount: number }[];
  pageParams: unknown[];
};

/**
 * Updates a content generation record directly in all matching React Query caches.
 * This avoids the loading state that would occur with invalidateQueries.
 */
export const updateContentGenerationRecordInCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  update: ContentGenerationUpdate
) => {
  // Update all queries that start with CONTENT_GENERATION_QUERY_KEY
  queryClient.setQueriesData<InfiniteContentGenerationData>(
    { queryKey: [CONTENT_GENERATION_QUERY_KEY] },
    (oldData) => {
      if (!oldData?.pages) return oldData;

      const updatedPages = oldData.pages.map((page) => ({
        ...page,
        records: page.records.map((record) => {
          // Match by skuContentGenerationId or retailerSku
          const isMatch =
            (update.skuContentGenerationId && record.skuContentGenerationId === update.skuContentGenerationId) ||
            (update.retailerSku && record.retailerSku === update.retailerSku);

          if (isMatch) {
            return {
              ...record,
              status: update.status as GenerationStatus,
              ...(update.skuContentGenerationId && { skuContentGenerationId: update.skuContentGenerationId }),
              ...(update.profileId && { profileId: update.profileId }),
              ...(update.profileName && { profileName: update.profileName }),
              ...(update.profileVersion !== undefined && { profileVersion: update.profileVersion }),
              ...(update.version !== undefined && { version: update.version }),
              ...(update.updatedAt && { updatedAt: update.updatedAt }),
              ...(update.projectId && { projectId: update.projectId })
            };
          }
          return record;
        })
      }));

      return {
        ...oldData,
        pages: updatedPages
      };
    }
  );
};

const useDebounceValue = (value: string, delay: number = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const useFilterChangeDetection = (appFilters: ReturnType<typeof useBeaconFilters>['appFilters'] | null) => {
  const prevFiltersRef = useRef<string | null>(null);
  const [filtersChanged, setFiltersChanged] = useState(false);

  const currentFiltersString = useMemo(() => {
    const normalizedFilters = {
      categoryFilters: appFilters?.categoryFilters ? [...appFilters.categoryFilters].sort() : null,
      subcategoryFilters: appFilters?.subcategoryFilters ? [...appFilters.subcategoryFilters].sort() : null,
      segmentFilters: appFilters?.segmentFilters ? JSON.stringify(appFilters.segmentFilters) : null,
      businessUnitFilters: appFilters?.businessUnitFilters ? JSON.stringify(appFilters.businessUnitFilters) : null,
      retailPriceFilter: appFilters?.retailPriceFilter ? JSON.stringify(appFilters.retailPriceFilter) : null
    };
    return JSON.stringify(normalizedFilters);
  }, [appFilters]);

  useEffect(() => {
    if (prevFiltersRef.current === null) {
      prevFiltersRef.current = currentFiltersString;
      setFiltersChanged(false);
    } else if (prevFiltersRef.current !== currentFiltersString) {
      prevFiltersRef.current = currentFiltersString;
      setFiltersChanged(true);
    } else {
      setFiltersChanged(false);
    }
  }, [currentFiltersString]);

  return filtersChanged;
};

const fetchProductData = async (
  appFilters: ReturnType<typeof useBeaconFilters>['appFilters'] | null,
  variant: 'default' | 'review' | 'edit' | 'modal',
  pageNumber?: number,
  pageSize?: number,
  searchTerm?: string,
  projectId?: string,
  retailerSkus?: string[],
  excludeRetailerSkus?: string[]
) => {
  const data = await getInitialProductData({
    variant,
    appFilters,
    pageNumber,
    pageSize,
    searchTerm,
    projectId,
    retailerSkus,
    excludeRetailerSkus
  });
  const adaptedData = adaptInitialData(data.records);

  return {
    records: adaptedData,
    totalCount: data.totalCount
  };
};

export type ProductTableStoreOverride = Partial<Pick<
  ContentGenerationStore,
  'selectedItems' | 'toggleItem' | 'setSelectedItems' | 'deselectAll' | 'updateContentGenerationStatus'
>>;

export interface MergeQueryParams {
  pageParam?: number;
  retailerSkus?: string[];
  excludeRetailerSkus?: string[];
  projectId?: string | null;
  appFilters?: ReturnType<typeof useBeaconFilters>['appFilters'] | null;
  variant?: 'default' | 'review' | 'edit' | 'modal';
  pageSize?: number;
  searchTerm?: string;
}

export type MergeQueryFn = (args: MergeQueryParams) => Promise<{
  records: ContentGenerationRecord[];
  totalCount: number;
}>;

export const useProductTable = ({
  variant = 'default',
  retailerSkus,
  overrideStatus,
  projectId,
  excludeRetailerSkus,
  overrideAppFilters,
  overrideStore,
  mergeQueryFn,
  mergeQueryOverrides,
  onEnableFinalReview,
  currentPageNumber = 1,
  pageSize = ENTITY_TABLE_PAGE_SIZE
}: {
  variant?: 'default' | 'review' | 'edit' | 'modal';
  retailerSkus?: string[];
  overrideStatus?: GenerationStatus;
  excludeRetailerSkus?: string[];
  projectId?: string | null;
  overrideAppFilters?: ReturnType<typeof useBeaconFilters>['appFilters'];
  overrideStore?: ProductTableStoreOverride;
  mergeQueryFn?: MergeQueryFn;
  mergeQueryOverrides?: Partial<Pick<MergeQueryParams, 'retailerSkus' | 'excludeRetailerSkus'>>;
  onEnableFinalReview?: () => void;
  currentPageNumber?: number;
  pageSize?: number;
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounceValue(searchQuery, 600);

  const storeState = useContentGenerationStore();

  const selectedItems = (overrideStore?.selectedItems ?? storeState.selectedItems);
  const toggleItemFn = (overrideStore?.toggleItem ?? storeState.toggleItem);
  const setSelectedItemsFn = (overrideStore?.setSelectedItems ?? storeState.setSelectedItems);
  const deselectAllFn = (overrideStore?.deselectAll ?? storeState.deselectAll);
  const updateContentGenerationStatus = (overrideStore?.updateContentGenerationStatus ?? storeState.updateContentGenerationStatus);

  const queryClient = useQueryClient();
  const { appFilters: globalAppFilters } = useBeaconFilters();
  const { currentSubtab } = useBeaconRoutes();

  // Use overrideAppFilters if provided, otherwise use global appFilters
  const appFilters = overrideAppFilters || globalAppFilters;

  // Dual fetch deprecated: rely on optional mergeQueryFn provided by parent

  const baseQueryKey = useMemo(
    () => [CONTENT_GENERATION_QUERY_KEY, variant, appFilters, debouncedSearchQuery, retailerSkus, projectId, pageSize],
    [variant, appFilters, debouncedSearchQuery, retailerSkus, projectId, pageSize]
  );

  const {
    data: infiniteQueryData,
    isLoading: isLoadingPrimary,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery({
    queryKey: baseQueryKey,
    queryFn: ({ pageParam = 1 }) => {
      return fetchProductData(
        appFilters,
        variant,
        pageParam,
        pageSize,
        debouncedSearchQuery,
        projectId,
        retailerSkus,
        excludeRetailerSkus
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.totalCount === pageSize ? allPages.length + 1 : undefined;
    },
    staleTime: 0,
    refetchOnWindowFocus: false
  });

  const mergeQueryKey = useMemo(
    () => [
      CONTENT_GENERATION_QUERY_KEY,
      variant,
      'merge',
      appFilters,
      debouncedSearchQuery,
      retailerSkus,
      projectId,
      pageSize,
      mergeQueryOverrides?.retailerSkus,
      mergeQueryOverrides?.excludeRetailerSkus
    ],
    [variant, appFilters, debouncedSearchQuery, retailerSkus, projectId, pageSize, mergeQueryOverrides?.retailerSkus, mergeQueryOverrides?.excludeRetailerSkus]
  );

  const { data: mergeQueryData, isLoading: isLoadingMerge } = useInfiniteQuery({
    queryKey: mergeQueryKey,
    queryFn: ({ pageParam = 1 }) => {
      if (!mergeQueryFn) {
        return Promise.resolve({ records: [], totalCount: 0 });
      }
      return mergeQueryFn({
        pageParam,
        appFilters,
        variant,
        pageSize,
        searchTerm: debouncedSearchQuery,
        projectId,
        retailerSkus,
        excludeRetailerSkus,
        ...mergeQueryOverrides
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.totalCount === pageSize ? allPages.length + 1 : undefined;
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
    enabled: !!mergeQueryFn
  });

  const isLoading = isLoadingPrimary || (!!mergeQueryFn && isLoadingMerge);

  const allData = useMemo(() => {
    if (!infiniteQueryData?.pages) return [];

    const primaryRecords = infiniteQueryData.pages.flatMap((page) => page.records);

    const mergeRecords = mergeQueryData?.pages ? mergeQueryData.pages.flatMap((page) => page.records) : [];

    const recordMap = new Map<string, ContentGenerationRecord>();

    primaryRecords.forEach((record) => {
      recordMap.set(record.retailerSku, record);
    });

    // Merge in optional mergeQuery results, without overriding primary
    mergeRecords.forEach((record) => {
      if (!recordMap.has(record.retailerSku)) {
        recordMap.set(record.retailerSku, record);
      }
    });

    // Convert back to array
    const mergedRecords = Array.from(recordMap.values());

    // Sort by updatedAt descending (newest first) so recently generated items appear at the top
    const sortedRecords = mergedRecords.sort((a, b) => {
      const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return dateB - dateA;
    });

    // Override status if provided (for backward compatibility or other use cases)
    if (overrideStatus) {
      return sortedRecords.map((record) => ({
        ...record,
        status: overrideStatus
      }));
    }
    return sortedRecords;
  }, [infiniteQueryData, mergeQueryData, overrideStatus]);

  const updateProfilesInCache = useCallback(
    (
      updates: {
        retailerSku: string;
        contentGenId: string;
        profileId: string;
        profileName: string;
        profileVersion: number;
        status?: GenerationStatus;
      }[]
    ) => {
      queryClient.setQueryData(
        baseQueryKey,
        (
          oldData:
            | { pages: { records: ContentGenerationRecord[]; totalCount: number }[]; pageParams: number[] }
            | undefined
        ) => {
          if (!oldData) return oldData;

          const updatesMap = new Map();
          updates.forEach((update) => {
            const key = update.retailerSku;
            updatesMap.set(key, update);
          });

          const updatedPages = oldData.pages.map((page) => ({
            ...page,
            records: page.records.map((item) => {
              const key = item.retailerSku;
              const update = updatesMap.get(key);

              if (update) {
                return {
                  ...item,
                  profileId: update.profileId,
                  profileName: update.profileName,
                  ...(update.status && { status: update.status }),
                  profileVersion: update?.profileVersion || item.profileVersion
                };
              }
              return item;
            })
          }));

          return {
            ...oldData,
            pages: updatedPages
          };
        }
      );
    },
    [queryClient, baseQueryKey]
  );

  const originalStates = useRef<
    Map<
      string,
      {
        status: GenerationStatus;
        profileId: string;
        profileName: string;
      }
    >
  >(new Map());

  const prevSelectedItems = useRef<Set<string>>(new Set());

  useEffect(() => {
    const currentSelected = selectedItems;
    const previousSelected = prevSelectedItems.current;

    const newlySelected = Array.from(currentSelected).filter((retailerSku) => !previousSelected.has(retailerSku));

    const newlyDeselected = Array.from(previousSelected).filter((retailerSku) => !currentSelected.has(retailerSku));

    const updates: {
      retailerSku: string;
      contentGenId: string;
      profileId: string;
      profileName: string;
      profileVersion: number;
      status: GenerationStatus;
    }[] = [];

    newlySelected.forEach((retailerSku) => {
      const item = allData.find((p) => p.retailerSku === retailerSku);
      if (!item) return;

      originalStates.current.set(retailerSku, {
        status: item.status,
        profileId: item.profileId || '',
        profileName: item.profileName || ''
      });

      if (!['READY_TO_REVIEW', 'REVIEWED'].includes(item.status)) {
        updates.push({
          retailerSku,
          contentGenId: item.skuContentGenerationId,
          profileId: item.profileId || '',
          profileName: item.profileName || '',
          profileVersion: item.profileVersion || 0,
          status: 'READY_TO_GENERATE'
        });
      }
    });

    newlyDeselected.forEach((retailerSku) => {
      const item = allData.find((p) => p.retailerSku === retailerSku);
      const originalState = originalStates.current.get(retailerSku);

      if (item && originalState) {
        // Don't restore original status if item is currently GENERATING - 
        // this preserves optimistic updates from content generation
        if (item.status === 'GENERATING') {
          originalStates.current.delete(retailerSku);
          return;
        }

        updates.push({
          retailerSku,
          contentGenId: item.skuContentGenerationId,
          profileId: originalState.profileId || '',
          profileName: originalState.profileName || '',
          profileVersion: item.profileVersion || 0,
          status: originalState.status
        });
        originalStates.current.delete(retailerSku);
      }
    });

    if (updates.length > 0) {
      updateProfilesInCache(updates);
    }

    prevSelectedItems.current = new Set(currentSelected);
  }, [selectedItems, allData, updateProfilesInCache]);

  const handleProfileAssignment = useCallback(
    (
      updates: {
        retailerSku: string;
        contentGenId: string;
        profileId: string;
        profileName: string;
        profileVersion?: number;
      }[]
    ) => {
      const enhancedUpdates = updates.map((update) => {
        const isSelected = selectedItems.has(update.retailerSku);
        const isAssigningProfile = update.profileId && update.profileName;
        return {
          ...update,
          profileVersion: update.profileVersion || 0,
          ...(isSelected && isAssigningProfile && { status: 'READY_TO_GENERATE' as const })
        };
      });

      updateProfilesInCache(enhancedUpdates);
    },
    [selectedItems, updateProfilesInCache]
  );

  useEffect(() => {
    let subscriptionObj: { unsubscribe: () => void } | null = null;

    const setupSubscription = async () => {
      subscriptionObj = await subscribeToUpdates({
        onMessage: (data: ContentGenerationUpdate) => {
          if (data && 'status' in data && data.skuContentGenerationId) {
            updateContentGenerationStatus(data.skuContentGenerationId, data.status as GenerationStatus);
            // Update content generation queries directly in cache to avoid loading state
            updateContentGenerationRecordInCache(queryClient, data);
            // Invalidate and force refetch ContentLibrary queries
            queryClient.invalidateQueries({ 
              queryKey: ['ContentLibrary'],
              refetchType: 'active'
            });
            // Invalidate project review comparison data
            queryClient.invalidateQueries({ 
              queryKey: ['PROJECT_REVIEW_COMPARE'],
              refetchType: 'active'
            });
            // Invalidate library review comparison data
            queryClient.invalidateQueries({ 
              queryKey: ['LIBRARY_REVIEW_COMPARE'],
              refetchType: 'active'
            });
            // Invalidate content generation item queries
            queryClient.invalidateQueries({ 
              queryKey: ['ContentGenerationItem'],
              refetchType: 'active'
            });
          }
        },
        onError: (error) => {
          console.error('❌ Subscription error in ContentGenerationProfile:', error);
        }
      });
    };

    setupSubscription();

    // Cleanup: unsubscribe when component unmounts or dependencies change
    return () => {
      if (subscriptionObj) {
        subscriptionObj.unsubscribe();
      }
    };
  }, [queryClient, updateContentGenerationStatus]);

  const updatedAllData = useMemo(() => {
    const data = allData.map((item) => ({
      ...item,
      checked: selectedItems.has(item.retailerSku)
    }));

    return data;
  }, [allData, selectedItems]);

  const getCurrentPageData = useCallback(
    (pageNumber: number, pageSize: number) => {
      const startIndex = (pageNumber - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      return updatedAllData.slice(startIndex, endIndex);
    },
    [updatedAllData]
  );

  const toggleHandler = useCallback(
    (retailerSku: string) => {
      toggleItemFn(retailerSku);
    },
    [toggleItemFn]
  );

  const setSelectedHandler = useCallback(
    (items: Set<string>) => {
      setSelectedItemsFn(items);
    },
    [setSelectedItemsFn]
  );

  const deselectAllHandler = useCallback(() => {
    deselectAllFn();
  }, [deselectAllFn]);

  const columnDefs = useMemo(() => {
    if (currentSubtab === BEACON_SUBTABS.GENERATE) {
      return getColumnDefsGenerate(
        updatedAllData,
        pageSize,
        currentPageNumber,
        selectedItems,
        toggleHandler,
        setSelectedHandler,
        deselectAllHandler,
        onEnableFinalReview
      );
    }

    if (variant === 'review') {
      return getColumnDefsReview(
        updatedAllData,
        pageSize,
        currentPageNumber,
        selectedItems,
        toggleHandler,
        setSelectedHandler,
        deselectAllHandler,
        onEnableFinalReview
      );
    }

    if (variant === 'edit') {
      return getColumnDefsEdit(
        updatedAllData,
        pageSize,
        currentPageNumber,
        selectedItems,
        toggleHandler,
        setSelectedHandler,
        deselectAllHandler,
        onEnableFinalReview
      );
    }

    if (variant === 'modal') {
      return getColumnDefsV2(
        updatedAllData,
        pageSize,
        currentPageNumber,
        selectedItems,
        toggleHandler,
        setSelectedHandler,
        deselectAllHandler,
        onEnableFinalReview,
        variant
      );
    }

    return getColumnDefsV2(
      updatedAllData,
      pageSize,
      currentPageNumber,
      selectedItems,
      toggleHandler,
      setSelectedHandler,
      deselectAllHandler,
      onEnableFinalReview
    );
  }, [updatedAllData, variant, currentSubtab, selectedItems, toggleHandler, setSelectedHandler, deselectAllHandler, onEnableFinalReview, currentPageNumber, pageSize]);

  const selectedCount = selectedItems.size;

  const readyCount = useMemo(() => {
    return updatedAllData.filter((item) => selectedItems.has(item.retailerSku)).length;
  }, [updatedAllData, selectedItems]);

  const readyToReviewCount = useMemo(() => {
    return updatedAllData.filter((item) => selectedItems.has(item.retailerSku)).length;
  }, [updatedAllData, selectedItems]);

  return {
    allData: updatedAllData,
    readyToReviewCount,
    getCurrentPageData,
    columnDefs,
    searchQuery,
    setSearchQuery,
    selectedCount,
    readyCount,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    updateProfilesInCache,
    handleProfileAssignment
  };
};
